import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ApiResponse } from '../../models/api-response.model';
import { AuthCheckResponse } from '../../models/auth-check-response.model';
import { GenerateRecipientOtpRequest } from '../../models/generate-recipient-otp-request.model';
import { RecipientOtpSessionResponse } from '../../models/recipient-otp-session-response.model';
import { RecipientOtpVerificationDetails } from '../../models/recipient-otp-verification.model';
import { VerifyRecipientOtpRequest } from '../../models/verify-recipient-otp-request.model';

@Injectable({ providedIn: 'root' })
export class RecipientOtpApiService {
    private readonly http = inject(HttpClient);

    createOtpSession(request: GenerateRecipientOtpRequest): Observable<RecipientOtpSessionResponse> {
        return this.http
            .post<ApiResponse<RecipientOtpSessionResponse>>(
                `${environment.apiBaseUrl}/recipients/me/otp`,
                request,
                { withCredentials: true }
            )
            .pipe(
                map((resp) => {
                    if (!resp.success) {
                        throw new Error(resp.error?.message ?? 'Failed to create OTP session');
                    }

                    return resp.result ?? {
                        challengeId: '',
                        maskedPhoneNumber: null
                    };
                })
            );
    }

    verifyOtp(request: VerifyRecipientOtpRequest): Observable<RecipientOtpVerificationDetails> {
        return this.http
            .post<ApiResponse<RecipientOtpVerificationDetails>>(
                `${environment.apiBaseUrl}/recipients/me/otp/verify`,
                request,
                { withCredentials: true }
            )
            .pipe(
                map((resp) => {
                    if (!resp.success) {
                        throw new Error(resp.error?.message ?? 'Failed to verify OTP');
                    }

                    return resp.result ?? {
                        isOtpCodeValid: false,
                        isOtpCodeExpired: false,
                        isOtpSessionLocked: false
                    };
                })
            );
    }

    checkAuth(): Observable<boolean> {
        return this.http
            .get<ApiResponse<AuthCheckResponse>>(
                `${environment.apiBaseUrl}/recipients/me/otp/check`,
                { withCredentials: true }
            )
            .pipe(
                map((resp) => {
                    if (!resp.success) {
                        return false;
                    }

                    return resp.result?.authenticated ?? false;
                })
            );
    }

    keepAlive(): Observable<void> {
        return this.http
            .post<ApiResponse<unknown>>(
                `${environment.apiBaseUrl}/recipients/me/otp/keep-alive`,
                null,
                { withCredentials: true }
            )
            .pipe(
                map((resp) => {
                    if (!resp.success) {
                        throw new Error(resp.error?.message ?? 'Failed to keep alive');
                    }

                    return undefined;
                })
            );
    }

    logout(): Observable<void> {
        return this.http
            .post<ApiResponse<unknown>>(
                `${environment.apiBaseUrl}/recipients/me/otp/logout`,
                null,
                { withCredentials: true }
            )
            .pipe(
                map((resp) => {
                    if (!resp.success) {
                        throw new Error(resp.error?.message ?? 'Failed to logout');
                    }

                    return undefined;
                })
            );
    }
}
